import { Suspense, lazy, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { BrandGlyph } from "./components/Icon";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { useSession } from "./lib/session";
import SignIn from "./routes/SignIn";
import Register from "./routes/Register";
import ForgotPassword from "./routes/ForgotPassword";
import ResetPassword from "./routes/ResetPassword";
import MfaEnrol from "./routes/MfaEnrol";
import MfaVerify from "./routes/MfaVerify";
import Catalogue from "./routes/Catalogue";
import CourseMap from "./routes/CourseMap";
import ModuleShell from "./routes/ModuleShell";
import ModuleOverview from "./routes/ModuleOverview";
import LessonReader from "./routes/LessonReader";
import QuizView from "./routes/QuizView";
import Certificate from "./routes/Certificate";
import Account from "./routes/Account";
import Feedback from "./routes/Feedback";
import NotFound from "./routes/NotFound";

/**
 * The lab carries CodeMirror and xterm, and the administration area is for a handful of people:
 * neither belongs in the bundle a learner downloads to read a lesson.
 */
const LabWorkspace = lazy(() => import("./routes/LabWorkspace"));
const AdminArea = lazy(() => import("./routes/admin/AdminArea"));

/** Where an unfinished sign-in belongs. */
function pendingPath(mfaEnrolled: boolean): string {
  return mfaEnrolled ? "/mfa" : "/mfa/set-up";
}

function Splash({ message }: { message: string }) {
  return (
    <div className="shell">
      <main className="main">
        <div className="wall" aria-busy="true">
          <p style={{ color: "var(--iris)" }}>
            <BrandGlyph className="brand-glyph" />
          </p>
          <h1>{message}</h1>
          <p className="row">
            <span className="spinner" />
            <span className="dim">One moment.</span>
          </p>
        </div>
      </main>
    </div>
  );
}

/** Nothing inside the app shell renders until the session is known to be full. */
function RequireFull({ children }: { children: ReactNode }) {
  const { loaded, step, mfaEnrolled, error } = useSession();
  const location = useLocation();

  if (!loaded) return <Splash message="Checking your session" />;
  if (step === "full") return <>{children}</>;

  const from = `${location.pathname}${location.search}`;
  if (step === "password") {
    return <Navigate to={pendingPath(mfaEnrolled)} replace state={{ from }} />;
  }
  return (
    <Navigate to="/sign-in" replace state={error ? undefined : { from }} />
  );
}

/** The sign-in screens: a signed-in learner is sent on to where they were going. */
function GuestOnly({ children }: { children: ReactNode }) {
  const { loaded, step, mfaEnrolled } = useSession();
  if (!loaded) return <Splash message="Checking your session" />;
  if (step === "full") return <Navigate to="/" replace />;
  if (step === "password") return <Navigate to={pendingPath(mfaEnrolled)} replace />;
  return <>{children}</>;
}

/** The MFA screens need a password-step session, and the right one of the two. */
function RequireMfaStep({ enrolled, children }: { enrolled: boolean; children: ReactNode }) {
  const { loaded, step, mfaEnrolled } = useSession();
  if (!loaded) return <Splash message="Checking your session" />;
  if (step === "none") return <Navigate to="/sign-in" replace />;
  if (step === "full") return <Navigate to="/" replace />;
  if (mfaEnrolled !== enrolled) return <Navigate to={pendingPath(mfaEnrolled)} replace />;
  return <>{children}</>;
}

function RequireAdmin({ children }: { children: ReactNode }) {
  const { isAdmin } = useSession();
  if (!isAdmin) {
    return (
      <div className="shell">
        <main className="main">
          <div className="wall">
            <p className="wall-code" aria-hidden="true">403</p>
            <h1>Administrators only</h1>
            <p>
              This area manages learners, invitations and capstone reviews. Your account does not
              have access to it.
            </p>
            <a className="btn btn-primary" href="/">
              Back to the courses
            </a>
          </div>
        </main>
      </div>
    );
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <ErrorBoundary>
      <Routes>
        <Route path="/sign-in" element={<GuestOnly><SignIn /></GuestOnly>} />
        <Route path="/register" element={<GuestOnly><Register /></GuestOnly>} />
        <Route path="/forgot-password" element={<GuestOnly><ForgotPassword /></GuestOnly>} />
        {/* A reset link has to work whatever state the browser is in. */}
        <Route path="/reset-password" element={<ResetPassword />} />

        <Route
          path="/mfa"
          element={<RequireMfaStep enrolled><MfaVerify /></RequireMfaStep>}
        />
        <Route
          path="/mfa/set-up"
          element={<RequireMfaStep enrolled={false}><MfaEnrol /></RequireMfaStep>}
        />

        <Route path="/" element={<RequireFull><Catalogue /></RequireFull>} />
        <Route path="/courses/:courseId" element={<RequireFull><CourseMap /></RequireFull>} />
        <Route
          path="/courses/:courseId/certificate"
          element={<RequireFull><Certificate /></RequireFull>}
        />
        <Route path="/modules/:moduleId" element={<RequireFull><ModuleShell /></RequireFull>}>
          <Route index element={<ModuleOverview />} />
          <Route path="lessons/:lessonId" element={<LessonReader />} />
          <Route path="quiz" element={<QuizView />} />
        </Route>
        <Route
          path="/modules/:moduleId/lab"
          element={
            <RequireFull>
              <Suspense fallback={<Splash message="Opening the lab workspace" />}>
                <LabWorkspace />
              </Suspense>
            </RequireFull>
          }
        />
        {/* Before there was more than one course, its certificate lived here. Links to it exist
            in emails and bookmarks, and it only ever meant the Terraform one. */}
        <Route path="/certificate" element={<Navigate to="/courses/terraform/certificate" replace />} />
        <Route path="/courses" element={<Navigate to="/" replace />} />
        <Route path="/account" element={<RequireFull><Account /></RequireFull>} />
        <Route path="/feedback" element={<RequireFull><Feedback /></RequireFull>} />
        <Route
          path="/admin/*"
          element={
            <RequireFull>
              <RequireAdmin>
                <Suspense fallback={<Splash message="Opening administration" />}>
                  <AdminArea />
                </Suspense>
              </RequireAdmin>
            </RequireFull>
          }
        />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </ErrorBoundary>
  );
}
